/**
 * Shop selection store.
 *
 * `selectedShopId` is `null` for "all my shops" (the default) or an Innu shop
 * UUID. It hydrates from `?shopId=` first, then
 * `localStorage["recruiting.selectedShopId"]`, and every change is mirrored
 * back to both. The list of selectable shops comes from `GET /api/shops`
 * (the worker resolves it from Innu User Access — admins see every shop).
 * The selector is UX only; the worker enforces scoping on every read.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { useApiClient } from './api-client';

export const LS_SHOP_KEY = 'recruiting.selectedShopId';

export interface ShopOption {
  id: string;
  name: string;
}

interface ShopStore {
  selectedShopId: string | null;
  hydrated: boolean;
  /** `null` = all shops. */
  setShop: (id: string | null) => void;
  hydrate: () => void;
}

function persistShop(id: string | null): void {
  try {
    if (id) localStorage.setItem(LS_SHOP_KEY, id);
    else localStorage.removeItem(LS_SHOP_KEY);
  } catch {
    // private mode — selection simply does not survive a reload
  }
  try {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('shopId', id);
    else url.searchParams.delete('shopId');
    window.history.replaceState(null, '', url.toString());
  } catch {
    // SSR guard
  }
}

function readInitialShopId(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('shopId');
    if (fromUrl) return fromUrl;
    return localStorage.getItem(LS_SHOP_KEY);
  } catch {
    return null;
  }
}

export const useShopStore = create<ShopStore>((set, get) => ({
  selectedShopId: null,
  hydrated: false,
  setShop: (id) => {
    set({ selectedShopId: id, hydrated: true });
    persistShop(id);
  },
  hydrate: () => {
    if (get().hydrated) return;
    set({ selectedShopId: readInitialShopId(), hydrated: true });
  },
}));

export const SHOPS_QUERY_KEY = ['shops'] as const;

/** Shops the current user may see. Envelope already unwrapped by the client. */
export function useShops() {
  const client = useApiClient();
  return useQuery({
    queryKey: SHOPS_QUERY_KEY,
    queryFn: () => client.get<ShopOption[]>('/api/shops'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mount-once hydration: read the URL/localStorage pick, and once the shop
 * list arrives drop a selection the user is no longer allowed to see.
 */
export function useShopBootstrap(): void {
  const hydrate = useShopStore((s) => s.hydrate);
  const setShop = useShopStore((s) => s.setShop);
  const selected = useShopStore((s) => s.selectedShopId);
  const { data: shops } = useShops();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!selected || !shops) return;
    if (!shops.some((s) => s.id === selected)) setShop(null);
  }, [shops, selected, setShop]);
}
